/* LöylyCraft gift packaging
   ------------------------------------------------------------------
   <loyly-gift-box>     cart block for REGULAR hats: select / deselect a $20 box,
                        choose how many boxes, add packing instructions.
                        Customers take as many boxes as they want, up to one per
                        regular hat - a box is never sold without a hat to fill it.
                        This block shows no status text: every action is visible
                        in the block itself, so a line of prose underneath only
                        added noise (and leaked raw cart-API errors).
   <loyly-pdp-gift>     PDP opt-in for REGULAR hats. Selecting it puts exactly ONE
                        box in the cart; adding more hats never adds a second one.
                        A box left without any regular hat is cleaned up by the
                        cart block, so nobody is charged for packaging alone.
   Luxury hats get a complimentary, dedicated box - rendered server-side and
   recorded on the order as a cart attribute. Nothing to click. */

(function () {
  const root = () => window.Shopify?.routes?.root || '/';
  const PACKING_PROPERTY = 'Packing instructions';
  const LUXURY_ATTRIBUTE = 'Gift packaging';
  const LUXURY_LABEL = 'Included free with Luxury Sheepskin Sauna Hat';

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(detail.description || 'Gift packaging could not be updated.');
    }
    return response.json();
  };

  const getCart = async () => {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    return response.json();
  };

  const findGiftLine = (cart, variantId) => cart.items.find((item) => item.variant_id === variantId);

  /* ---- section rendering: refresh the cart in place instead of reloading ---- */

  // The cart page renders items and summary from ONE section, so both containers
  // carry the same section id. Read each one back through its own container id,
  // otherwise the summary gets filled with the item list.
  const sectionsToRender = () => {
    const sections = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items) {
      sections.push({ id: 'main-cart-items', section: items.dataset.id, source: '#main-cart-items .js-contents' });
    }
    if (footer) {
      sections.push({ id: 'main-cart-footer', section: footer.dataset.id, source: '#main-cart-footer .js-contents' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', source: '.shopify-section' });
    }
    return sections;
  };

  const applySections = (payload) => {
    const rendered = payload && payload.sections;
    if (!rendered) return false;
    sectionsToRender().forEach(({ id, section, source }) => {
      const html = rendered[section];
      const target = document.getElementById(id);
      if (!html || !target) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector(source);
      const destination = id === 'cart-icon-bubble' ? target : target.querySelector('.js-contents');
      if (fresh && destination) destination.innerHTML = fresh.innerHTML;
    });
    return true;
  };

  const withSections = (body) => ({
    ...body,
    sections: [...new Set(sectionsToRender().map((entry) => entry.section))],
    sections_url: window.location.pathname,
  });

  /* ---------------------------- cart block ---------------------------- */

  if (!customElements.get('loyly-gift-box')) {
    customElements.define(
      'loyly-gift-box',
      class LoylyGiftBox extends HTMLElement {
        connectedCallback() {
          this.giftVariantId = Number(this.dataset.giftVariantId);
          this.giftKey = this.dataset.giftKey || '';
          this.quantity = Number(this.dataset.giftQuantity || 0);
          this.maxBoxes = Number(this.dataset.maxBoxes || 1);
          this.instructionsPanel = this.querySelector('[data-instructions-panel]');
          this.instructionsField = this.querySelector('[data-instructions-field]');
          this.savedInstructions = (this.instructionsField?.value || '').trim();

          // No status line in the cart block: the block's own state is the feedback.
          this.querySelector('[data-gift-status]')?.remove();

          this.addEventListener('click', this.onClick);
          // Autosave: nobody should lose their note because they went straight to
          // checkout without pressing Save.
          this.instructionsField?.addEventListener('input', this.onInstructionsInput);
          this.instructionsField?.addEventListener('blur', this.autosaveInstructions);
          this.syncLuxuryAttribute();
          this.renderEntry(this.savedInstructions !== '');

          if (this.dataset.orphan === 'true') {
            // Nothing left to pack: drop the stray box rather than charge for it.
            this.setQuantity(0);
          } else if (this.quantity > this.maxBoxes) {
            // Hats were removed after the boxes were chosen. Boxes may never
            // outnumber hats, so bring the box count back down to match.
            this.setQuantity(this.maxBoxes);
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.instructionsField?.removeEventListener('input', this.onInstructionsInput);
          this.instructionsField?.removeEventListener('blur', this.autosaveInstructions);
          window.clearTimeout(this.saveTimer);
        }

        setBusy(isBusy) {
          this.classList.toggle('is-busy', isBusy);
        }

        // Add / Edit is driven by what is actually stored, not by what the page
        // happened to render on load: an autosaved note never triggers a
        // re-render, so the control has to be rewritten here.
        renderEntry = (hasValue) => {
          if (!this.instructionsPanel) return;
          let entry = this.querySelector('[data-instructions-entry]');
          if (!entry) {
            entry = document.createElement('div');
            entry.setAttribute('data-instructions-entry', '');
            this.instructionsPanel.parentNode.insertBefore(entry, this.instructionsPanel);
            const stale = entry.previousElementSibling;
            if (
              stale &&
              (stale.classList.contains('loyly-gift__saved') ||
                stale.classList.contains('loyly-gift__instructions-toggle'))
            ) {
              stale.remove();
            }
          }
          entry.innerHTML = hasValue
            ? '<span class="loyly-gift__saved">' +
              '<span aria-hidden="true">&#10003;</span>' +
              '<span class="loyly-gift__saved-text">Packing instructions added</span>' +
              '<button class="loyly-gift__instructions-toggle" type="button" data-gift-action="toggle-instructions">Edit</button>' +
              '</span>'
            : '<button class="loyly-gift__instructions-toggle" type="button" data-gift-action="toggle-instructions">' +
              '<span aria-hidden="true">+</span> Add packing instructions' +
              '</button>';
        };

        // Luxury packaging is complimentary and dedicated to its hat. It is recorded
        // as a cart attribute rather than a product line, because adding the Gift
        // Packaging product for it used to trip a cart transform that split the hat
        // into one line per unit.
        syncLuxuryAttribute = async () => {
          const wanted = this.dataset.hasLuxury === 'true' ? LUXURY_LABEL : '';
          if (this.dataset.luxuryAttribute === wanted) return;
          try {
            await postJSON(`${root()}cart/update.js`, { attributes: { [LUXURY_ATTRIBUTE]: wanted } });
            this.dataset.luxuryAttribute = wanted;
          } catch (error) {
            /* non-blocking: the attribute is a fulfillment note, not part of pricing */
          }
        };

        onClick = (event) => {
          const action = event.target.closest('[data-gift-action]')?.dataset.giftAction;
          if (!action) return;
          event.preventDefault();

          if (action === 'select') this.select();
          if (action === 'deselect') this.setQuantity(0);
          if (action === 'increase') this.setQuantity(this.quantity + 1);
          if (action === 'decrease') this.setQuantity(this.quantity - 1);
          if (action === 'toggle-instructions') this.toggleInstructions();
          if (action === 'save-instructions') this.saveInstructions();
        };

        // Line keys are minted fresh whenever a line's properties change, so a key
        // captured at render time goes stale the moment a note is saved. Every
        // write resolves the key from the live cart instead - that stale key was
        // what surfaced "no valid id or line parameter".
        resolveGiftLine = async () => {
          const cart = await getCart();
          const line = findGiftLine(cart, this.giftVariantId);
          this.giftKey = line?.key || '';
          this.quantity = line?.quantity || 0;
          return line;
        };

        select = async () => {
          if (this.quantity > 0) return;
          this.setBusy(true);
          try {
            const payload = await postJSON(
              `${root()}cart/add.js`,
              withSections({ items: [{ id: this.giftVariantId, quantity: 1 }] })
            );
            if (!applySections(payload)) window.location.reload();
          } catch (error) {
            this.setBusy(false);
          }
        };

        setQuantity = async (next) => {
          const target = next <= 0 ? 0 : Math.min(Math.max(next, 1), this.maxBoxes);
          if (target === this.quantity) return;
          this.setBusy(true);
          try {
            const line = await this.resolveGiftLine();
            if (!line) {
              this.setBusy(false);
              return;
            }
            if (target === this.quantity) {
              this.setBusy(false);
              return;
            }
            if (target === 0) await this.writeAttribute('');
            const payload = await postJSON(
              `${root()}cart/change.js`,
              withSections({ id: this.giftKey, quantity: target })
            );
            if (!applySections(payload)) window.location.reload();
          } catch (error) {
            this.setBusy(false);
          }
        };

        toggleInstructions = () => {
          if (!this.instructionsPanel) return;
          const open = this.instructionsPanel.hidden;
          this.instructionsPanel.hidden = !open;
          if (open) this.instructionsField?.focus();
        };

        writeAttribute = async (value) =>
          postJSON(`${root()}cart/update.js`, { attributes: { [PACKING_PROPERTY]: value } });

        onInstructionsInput = () => {
          window.clearTimeout(this.saveTimer);
          this.saveTimer = window.setTimeout(this.autosaveInstructions, 700);
        };

        autosaveInstructions = () => this.saveInstructions({ close: false });

        // The note is written twice on purpose, in one request pair so the two can
        // never disagree: as a cart attribute it lands in the order's Additional
        // details, as a line property it sits on the gift-box line itself and on
        // the packing slip. Fulfillment reads whichever they look at first.
        saveInstructions = async ({ close = true } = {}) => {
          if (!this.instructionsField) return;
          window.clearTimeout(this.saveTimer);
          const value = this.instructionsField.value.trim();
          if (value === this.savedInstructions) {
            if (close) this.toggleInstructions();
            return;
          }
          this.setBusy(true);
          try {
            const line = await this.resolveGiftLine();
            if (!line) {
              this.setBusy(false);
              return;
            }
            await this.writeAttribute(value);
            const payload = await postJSON(`${root()}cart/change.js`, {
              id: this.giftKey,
              quantity: this.quantity,
              // change.js ignores an empty properties object, so clearing has to
              // send the key with an empty value - Shopify then stores nothing.
              properties: { [PACKING_PROPERTY]: value },
            });
            this.savedInstructions = value;
            // Changing properties mints a new line key - keep ours current.
            const fresh = payload.items?.find((item) => item.variant_id === this.giftVariantId);
            if (fresh) this.giftKey = fresh.key;
            this.renderEntry(value !== '');
            if (close && this.instructionsPanel) this.instructionsPanel.hidden = true;
          } catch (error) {
            /* silent: the note is a nicety, and a raw API string helps nobody */
          } finally {
            this.setBusy(false);
          }
        };
      }
    );
  }

  /* ------------------------------ PDP opt-in ------------------------------ */

  if (!customElements.get('loyly-pdp-gift')) {
    customElements.define(
      'loyly-pdp-gift',
      class LoylyPdpGift extends HTMLElement {
        connectedCallback() {
          this.giftVariantId = Number(this.dataset.giftVariantId);
          this.status = this.querySelector('[data-gift-status]');
          this.selected = false;

          this.addEventListener('click', this.onClick);
          this.reflectCart();
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
        }

        setSelected(value) {
          this.selected = value;
          this.classList.toggle('is-selected', value);
          this.querySelector('[data-gift-action]')?.setAttribute('aria-pressed', String(value));
        }

        setBusy(isBusy, message = '') {
          this.classList.toggle('is-busy', isBusy);
          if (this.status) this.status.textContent = message;
        }

        // One box per cart. A box already there counts as selected, so adding a
        // second hat from this page never queues up a second box.
        reflectCart = async () => {
          try {
            const cart = await getCart();
            this.giftLine = findGiftLine(cart, this.giftVariantId);
            if (this.giftLine) this.setSelected(true);
          } catch (error) {
            /* leave the block in its default state */
          }
        };

        refreshCartCount = (payload) => {
          const bubble = document.getElementById('cart-icon-bubble');
          const html = payload?.sections?.['cart-icon-bubble'];
          if (!bubble || !html) return;
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const source = parsed.querySelector('.shopify-section');
          if (source) bubble.innerHTML = source.innerHTML;
        };

        onClick = (event) => {
          if (!event.target.closest('[data-gift-action]')) return;
          event.preventDefault();
          if (this.selected) {
            this.remove_();
          } else {
            this.add();
          }
        };

        add = async () => {
          this.setBusy(true, 'Adding your gift box…');
          try {
            const cart = await getCart();
            let line = findGiftLine(cart, this.giftVariantId);
            if (!line) {
              const payload = await postJSON(`${root()}cart/add.js`, {
                items: [{ id: this.giftVariantId, quantity: 1 }],
                sections: ['cart-icon-bubble'],
                sections_url: window.location.pathname,
              });
              this.refreshCartCount(payload);
              line = { key: payload.items?.[0]?.key };
            }
            this.giftLine = line;
            this.setSelected(true);
            this.setBusy(false, 'Gift box added — adjust quantity in your cart.');
          } catch (error) {
            this.setBusy(false, error.message);
          }
        };

        remove_ = async () => {
          this.setBusy(true, 'Removing gift packaging…');
          try {
            const cart = await getCart();
            const line = findGiftLine(cart, this.giftVariantId);
            if (line) {
              const payload = await postJSON(`${root()}cart/change.js`, {
                id: line.key,
                quantity: 0,
                sections: ['cart-icon-bubble'],
                sections_url: window.location.pathname,
              });
              this.refreshCartCount(payload);
            }
            this.giftLine = null;
            this.setSelected(false);
            this.setBusy(false, '');
          } catch (error) {
            this.setBusy(false, error.message);
          }
        };
      }
    );
  }

  /* ------------------------- stray-box cleanup ------------------------- */

  // The cart block only renders while there are regular hats to pack. If a paid
  // box outlives them - the last hat was removed, or the box was picked on a
  // product page and the hat never followed - take it back out instead of
  // charging for packaging alone. Luxury hats do not keep a paid box alive:
  // theirs is included.
  let cleaning = false;

  const cleanUpStrayBox = async () => {
    if (cleaning) return;
    if (!document.querySelector('.loyly-cart')) return;
    if (document.querySelector('loyly-gift-box')) return;
    cleaning = true;
    try {
      const cart = await getCart();
      const stray = cart.items.find((item) => item.handle === 'gift-packaging');
      if (!stray) {
        cleaning = false;
        return;
      }
      await postJSON(`${root()}cart/update.js`, { attributes: { [PACKING_PROPERTY]: '' } });
      await postJSON(`${root()}cart/change.js`, { id: stray.key, quantity: 0 });
      window.location.reload();
    } catch (error) {
      cleaning = false;
    }
  };

  // Removing a line re-renders the cart in place, without a page load, so watch
  // the item list too - checking only on load would leave the stray box behind.
  const watchCart = () => {
    cleanUpStrayBox();
    const items = document.getElementById('main-cart-items');
    if (!items) return;
    new MutationObserver(cleanUpStrayBox).observe(items, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCart, { once: true });
  } else {
    watchCart();
  }
})();
